/**
 * @file ShopDashboard.tsx
 * @description «Сводка» — главная кабинета магазина (phase10; phase19.31 —
 * разбор A макет (г), SC47, SC55).
 *
 * Отвечает на вопрос «как идут дела и что требует меня»:
 *  - заголовок в верхней панели: «Сводка», под ним «<магазин> · <день>»;
 *    главного действия нет;
 *  - до четырёх плиток показателей с переключателем периода («Выручка», не
 *    «GMV»; дубли «Выручка 7 дней» и «GMV 30 дней» убраны);
 *  - «Требует внимания» — из тех же чисел, что счётчики меню, и «В работе»
 *    (открытые заказы; очередь склада — только с правом на склад);
 *  - «Последние заказы» теми же ячейками, что реестр, и «Топ-10 товаров»;
 *  - сразу под заголовком — плашка «Сайт работает без: …» (SB125, TD-407;
 *    SB120 этап 2, uiux-sb120-stage2 §4): «что требует меня» — первым;
 *    только при праве смотреть черновик вида сайта (`ShopLiveChecksNotice`).
 * Ширина — общая колонка кабинета до 1600px (SC55). Телефон: плитки 2×2,
 * блоки столбиком, заказы — компактным списком. Данные — `useShopDashboard`,
 * блоки — `components/shop-dashboard/*`.
 */

import { useParams } from "react-router";

import { useCurrentContext, useHasPermission } from "../../auth/hooks";
import { dashboardAttention, dashboardWork } from "../../components/shop-dashboard/dashboard-model";
import { ShopDashboardMetrics } from "../../components/shop-dashboard/ShopDashboardMetrics";
import { ShopDashboardRecentOrders } from "../../components/shop-dashboard/ShopDashboardRecentOrders";
import { ShopDashboardTopSkus } from "../../components/shop-dashboard/ShopDashboardTopSkus";
import { useShopDashboard } from "../../components/shop-dashboard/useShopDashboard";
import { ShopLiveChecksNotice } from "../../components/site-view/ShopLiveChecksNotice";
import { AttentionList } from "../../components/ui/AttentionList";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { NoticeStrip } from "../../components/ui/NoticeStrip";
import { SectionCard } from "../../components/ui/SectionCard";
import { getShopTimeZone } from "../../lib/shop-time";

/** «четверг, 2 октября» — сегодня по поясу магазина (SC31). */
function todayText(): string {
  return new Date().toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long", timeZone: getShopTimeZone() });
}

export function ShopDashboard() {
  const ctx = useCurrentContext();
  const { slug } = useParams<{ slug: string }>();
  const canView = useHasPermission("SHOP_VIEW_ORDERS");
  const canManage = useHasPermission("SHOP_MANAGE_ORDERS");
  const canViewOrders = canView || canManage;
  const canWms = useHasPermission("SHOP_WMS_VIEW");
  const dashboard = useShopDashboard(canViewOrders);
  const base = `/shop/${slug}`;

  if (!ctx?.shop) return null;

  const attention = dashboardAttention({ counters: dashboard.counters, myTurn: dashboard.myTurn }, base);
  const work = dashboardWork(
    {
      openOrders: canViewOrders && dashboard.stats ? dashboard.stats.openOrdersCount : null,
      pickingQueue: dashboard.counters.pickingQueue,
      canWms,
    },
    base,
  );

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <CabinetPageHeader title="Сводка" subtitle={`${ctx.shop.displayName} · ${todayText()}`} />
      <ShopLiveChecksNotice />

      {canViewOrders ? (
        <ShopDashboardMetrics
          stats={dashboard.stats}
          period={dashboard.period}
          statsPeriod={dashboard.statsPeriod}
          onPeriodChange={dashboard.setPeriod}
          loading={dashboard.loading}
          error={dashboard.statsError}
          onRetry={dashboard.reload}
        />
      ) : (
        <NoticeStrip tone="info">Показатели заказов видят сотрудники с доступом к заказам.</NoticeStrip>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
        <SectionCard title="Требует внимания">
          <AttentionList aria-label="Требует внимания" items={attention} loading={dashboard.attentionLoading} />
        </SectionCard>
        {work.length > 0 ? (
          <SectionCard title="В работе">
            <AttentionList aria-label="В работе" items={work} />
          </SectionCard>
        ) : null}
      </div>

      {/* Как в макете (г): заказы и топ — во всю ширину колонки, друг под другом. */}
      {canViewOrders ? (
        <>
          <ShopDashboardRecentOrders
            orders={dashboard.recent}
            error={dashboard.recentError}
            onRetry={dashboard.reload}
            ordersBase={`${base}/orders`}
          />
          <ShopDashboardTopSkus top={dashboard.top} error={dashboard.topError} days={dashboard.statsPeriod} />
        </>
      ) : null}
    </div>
  );
}
